import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { usePackages } from '@/features/payments/hooks';
import { useNavigate } from '@tanstack/react-router';

const PricingCards: React.FC = () => {
  const { data: packages = [], isLoading, error } = usePackages();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const navigate = useNavigate();

  // Auto-select the second package when packages load
  useEffect(() => {
    if (packages.length > 1 && !selectedPlan) {
      setSelectedPlan(packages[1].id);
    }
  }, [packages, selectedPlan]);


  const handlePlanSelect = (planId: string): void => {
    setSelectedPlan(planId);
    // Redirect to signup page
    navigate({ to: '/sign-up' });
  };


  // Loading state
  if (isLoading) {
    return (
      <div id='pricing' className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div id='pricing' className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{(error as Error).message || 'Failed to load packages.'}</p>
          <button onClick={() => window.location.reload()} className="text-indigo-600 hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id='pricing' className="min-h-screen bg-white flex items-center justify-center px-5 py-4 md:p-8">
      <div className="max-w-7xl w-full">
        {/* Header */}
        <h1 className="text-xl md:text-5xl font-bold text-gray-900 text-center mb-8 md:mb-16">
          Pricing
        </h1>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {packages.map((pkg) => {
            const isSelected = selectedPlan === pkg.id;
            
            return (
              <div
                key={pkg.id}
                onClick={() => handlePlanSelect(pkg.id)}
                className={`
                  rounded-2xl p-4 md:p-8 cursor-pointer transition-all duration-300 ease-in-out
                  transform hover:scale-105 hover:-translate-y-1 relative shadow-xl
                  ${isSelected 
                    ? 'bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-500 shadow-lg hover:shadow-xl' 
                    : 'bg-gray-50 border border-gray-200 hover:border-indigo-200 shadow-sm hover:shadow-lg'
                  }
                `}
              >
                {/* 50% OFF Badge */}
                <div className="absolute top-0 right-0 bg-green-700 text-white text-xs font-bold px-3 py-1 rounded-bl-xl rounded-tr-xl z-10">
                  50% OFF (1st 6 Mo)
                </div>

                {/* Plan Header */}
                <div className="mb-6">
                  <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">
                    {pkg.name}
                  </h2>
                  <p className="text-gray-600">
                    {pkg.description}
                  </p>
                </div>

                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl md:text-5xl font-bold text-gray-900">
                      $ {pkg.price}
                    </span>
                    <span className="text-gray-600">
                      per<br/>{pkg.interval}
                    </span>
                  </div>
                </div>

                {/* Get Plan Button */}
                <button
                  className={`
                    w-full py-4 px-6 rounded-xl font-semibold transition-all duration-200 mb-8
                    ${isSelected
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-white text-indigo-600 border-2 border-indigo-600 hover:bg-indigo-50'
                    }
                  `}
                >
                  Get plan
                </button>

                {/* Features List */}
                <div className="space-y-4">
                  {pkg.displayFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <Check className="w-5 h-5 text-green-500" strokeWidth={2.5} />
                      </div>
                      <span className="text-gray-700 text-sm leading-relaxed">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingCards;